import React, { useEffect, useState } from "react";
import { CreateEmailCycleDrawer } from "./drawers/CreateEmailCycleDrawer";
import { observer } from "mobx-react";
import { useCreateEmailCycleStore } from "../stores/Hooks";
import { ScheduleEmailDialog } from "./dialogs/ScheduleEmailDialog";
import { useEmailTemplateDetailsStore } from "../../emailTemplates/stores/Hooks";
import { LoadingIndicator } from "@surya-digital/leo-reactjs-material-ui";
import {
  processProjectParams,
  useRouteNavigator,
} from "../../../routes/RoutesHelper";
import { CreateEmailCycleRPC } from "@pulse/pulse-rpcs/lib/email-cycle/createEmailCycleRPC";
import { UnexpectedErrorDialog } from "../../../components/dialogs/UnexpectedErrorDialog";
import { useTranslation } from "react-i18next";
import { ScheduleType } from "../stores/CreateEmailCycleStore";
import { ErrorDialog } from "../../../components/dialogs/ErrorDialog";

interface CreateEmailCycleProps {
  setOpenEmailQueuedSuccessfulDialog: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  closeFlow: VoidFunction;
}

export const CreateEmailCycle: React.FC<CreateEmailCycleProps> = observer(
  ({ setOpenEmailQueuedSuccessfulDialog, closeFlow }) => {
    const [openCreateEmailCycleDrawer, setOpenCreateEmailCycleDrawer] =
      useState(true);
    const [openScheduleEmailDialog, setOpenScheduleEmailDialog] =
      useState(false);
    const [openInvalidScheduleErrorDialog, setOpenInvalidScheduleErrorDialog] =
      useState(false);
    const [openUnexpectedErrorDialog, setOpenUnexpectedErrorDialog] =
      useState(false);

    const { t } = useTranslation();
    const navigateTo = useRouteNavigator();

    const createEmailCycleStore = useCreateEmailCycleStore();
    const emailTemplateDetailsStore = useEmailTemplateDetailsStore();

    useEffect(
      () => () => {
        createEmailCycleStore.resetStore();
        emailTemplateDetailsStore.resetStore();
      },
      [],
    );

    const handleCreateEmailTemplate = (): void => {
      processProjectParams(createEmailCycleStore.createEmailCycle, navigateTo)
        .then(() => {
          const validationError =
            createEmailCycleStore.emailCycleName.validationError ||
            createEmailCycleStore.senderEmailId.validationError ||
            createEmailCycleStore.senderEmailIdRPCError;
          if (validationError) {
            setOpenCreateEmailCycleDrawer(true);
            return;
          }

          if (createEmailCycleStore.createEmailCycleRPCError) {
            switch (createEmailCycleStore.createEmailCycleRPCError) {
              case CreateEmailCycleRPC.RPCError.InvalidProjectId:
                navigateTo.pageNotFound();
                break;
              case CreateEmailCycleRPC.RPCError.ProjectAlreadyArchived:
                setOpenUnexpectedErrorDialog(true);
                break;
              case CreateEmailCycleRPC.RPCError.InvalidSchedule:
                setOpenInvalidScheduleErrorDialog(true);
                break;
              default:
                createEmailCycleStore.createEmailCycleRPCError satisfies never;
            }
            return;
          }

          setOpenEmailQueuedSuccessfulDialog(true);
        })
        .catch(() => setOpenUnexpectedErrorDialog(true));
    };

    return (
      <>
        <CreateEmailCycleDrawer
          onClose={closeFlow}
          open={openCreateEmailCycleDrawer}
          onScheduleEmail={(scheduleType: ScheduleType) => {
            createEmailCycleStore.updateScheduleType(scheduleType);
            setOpenCreateEmailCycleDrawer(false);
            setOpenScheduleEmailDialog(true);
          }}
        />
        <ScheduleEmailDialog
          open={openScheduleEmailDialog}
          onSubmit={() => {
            setOpenScheduleEmailDialog(false);
            handleCreateEmailTemplate();
          }}
          onCancel={() => {
            setOpenCreateEmailCycleDrawer(true);
            setOpenScheduleEmailDialog(false);
            createEmailCycleStore.resetScheduleType();
          }}
        />

        <LoadingIndicator
          variant="overlay"
          isLoading={createEmailCycleStore.loadingCreateEmailCycle}
        />

        {openInvalidScheduleErrorDialog && (
          <ErrorDialog
            title={t("emailCycles.invalidScheduleId")}
            message={t("emailCycles.invalidScheduleIdErrorMessage")}
            onClickClose={() => {
              createEmailCycleStore.resetRPCError();
              setOpenScheduleEmailDialog(true);
            }}
          />
        )}

        {openUnexpectedErrorDialog && (
          <UnexpectedErrorDialog
            message={t("emailCycles.createEmailCycleUnexpectedErrorMessage")}
            onClickDone={closeFlow}
          />
        )}
      </>
    );
  },
);
